/**
 * Client-side V2 experience preference, stored in localStorage under
 * `dacw:v2Enabled`. Tri-valued in effect:
 *
 * - `"true"`  — user explicitly opted in to V2
 * - `"false"` — user explicitly opted out (Legacy)
 * - missing   — no explicit preference; defaults to V2
 *
 * V2 is the default landing experience, so `isV2Enabled()` returns
 * `true` unless storage explicitly says `"false"`. Storage failures
 * and undefined-`window` (SSR) also fall through to the default.
 *
 * The key retains its historical name for consistency with the rest
 * of the codebase and telemetry queries; the semantics changed from
 * "opt-in or off" to "opt-out or default V2".
 *
 * Retire this module once Legacy `<Content/>` is removed.
 */

const V2_ENABLED_KEY = "dacw:v2Enabled";
const OPT_IN_QUERY_PARAM = "v2";

// Any value not in these sets is treated as "no change" so a stray
// `?v2=` in a copy-pasted link cannot silently revoke a preference.
const OPT_IN_VALUES = new Set(["1", "true"]);
const OPT_OUT_VALUES = new Set(["0", "false"]);

/**
 * Read `?v2=...` from the current URL, persist to localStorage, and
 * strip the param via `history.replaceState`. Idempotent no-op when
 * the param is absent, unrecognized, or when storage/history APIs
 * are unavailable. Called from `main.tsx` once before React mounts.
 */
export function applyV2OptInFromUrl(): void {
  if (typeof window === "undefined") return;
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  const value = url.searchParams.get(OPT_IN_QUERY_PARAM);
  if (value === null) return;

  const normalized = value.toLowerCase();
  try {
    if (OPT_IN_VALUES.has(normalized)) {
      window.localStorage.setItem(V2_ENABLED_KEY, "true");
    } else if (OPT_OUT_VALUES.has(normalized)) {
      // Persist the opt-out explicitly so it survives the "missing =
      // default V2" behavior — removing the key would collapse into
      // the default and silently flip the user back to V2.
      window.localStorage.setItem(V2_ENABLED_KEY, "false");
    } else {
      return; // unrecognized value: leave storage and URL alone
    }
  } catch {
    // storage blocked - still strip the URL param so testers do not
    // see a confusing `?v2=1` linger after clicking the bookmark.
  }

  url.searchParams.delete(OPT_IN_QUERY_PARAM);
  try {
    window.history.replaceState(null, "", url.toString());
  } catch {
    // history API missing (very old browsers): non-fatal.
  }
}

/**
 * Synchronous V2 Route gate. Returns `true` unless storage explicitly
 * says `"false"`. Storage errors and SSR resolve to `true` (the
 * default), matching the new "V2 by default" behavior.
 */
export function isV2Enabled(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(V2_ENABLED_KEY) !== "false";
  } catch {
    return true;
  }
}

/**
 * Persist the V2 preference from a UI control (e.g. the in-app
 * toggle). Writes an explicit `"true"` / `"false"` so both directions
 * survive the "missing = default" behavior. Storage errors are
 * swallowed — matches `isV2Enabled`'s tolerance so a blocked storage
 * layer degrades to "flag stays whatever it was" rather than
 * surfacing a runtime error to the user.
 */
export function setV2Enabled(enabled: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(V2_ENABLED_KEY, enabled ? "true" : "false");
  } catch {
    // storage blocked (private mode, enterprise policy): non-fatal.
  }
}
